import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ArrowLeft, Cookie, Shield, Lock } from 'lucide-react';
import { Link } from 'react-router-dom';

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

export const CookiesPage = () => {
  const { t } = useTranslation();

  const cookieTypes = [
    {
      icon: Shield,
      titleKey: 'authCookiesTitle',
      descKey: 'authCookiesDesc',
      color: 'var(--th-secondary)'
    },
    {
      icon: Lock,
      titleKey: 'securityCookiesTitle',
      descKey: 'securityCookiesDesc',
      color: 'var(--th-primary)'
    }
  ];

  return (
    <div 
      className="min-h-screen py-20"
      style={{ backgroundColor: 'var(--th-background)' }}
      data-testid="cookies-page"
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 mb-8 font-medium transition-colors hover:underline"
          style={{ color: 'var(--th-secondary)' }}
          data-testid="cookies-back-to-home"
        >
          <ArrowLeft className="w-5 h-5" />
          {t('cookies.backToHome')}
        </Link>

        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
        >
          {/* Header */}
          <motion.div variants={fadeInUp} className="mb-12">
            <div className="flex items-center gap-4 mb-4">
              <div 
                className="w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg"
                style={{ backgroundColor: 'var(--th-primary)' }}
              >
                <Cookie className="w-8 h-8" style={{ color: '#1E293B' }} />
              </div>
              <h1 
                className="font-heading text-4xl md:text-5xl font-bold"
                style={{ color: 'var(--th-text-primary)' }}
              >
                {t('cookies.pageTitle')}
              </h1>
            </div>
            <p 
              className="text-sm"
              style={{ color: 'var(--th-text-muted)' }}
            >
              {t('cookies.lastUpdated')}
            </p>
          </motion.div>

          {/* Introduction */}
          <motion.div 
            variants={fadeInUp}
            className="p-6 rounded-2xl mb-8 border-2"
            style={{ 
              backgroundColor: 'var(--th-card-bg)',
              borderColor: 'var(--th-card-border)'
            }}
          >
            <p 
              className="text-lg leading-relaxed"
              style={{ color: 'var(--th-text-secondary)' }}
            >
              {t('cookies.intro')}
            </p>
          </motion.div>

          {/* What are Cookies Section */}
          <motion.section variants={fadeInUp} className="mb-10">
            <h2 
              className="font-heading text-2xl font-bold mb-4"
              style={{ color: 'var(--th-text-primary)' }}
            >
              {t('cookies.whatAreCookiesTitle')}
            </h2>
            <p 
              className="leading-relaxed"
              style={{ color: 'var(--th-text-secondary)' }}
            >
              {t('cookies.whatAreCookiesDesc')}
            </p>
          </motion.section>

          {/* Essential Cookies Section */}
          <motion.section variants={fadeInUp} className="mb-10">
            <h2 
              className="font-heading text-2xl font-bold mb-6"
              style={{ color: 'var(--th-text-primary)' }}
            >
              {t('cookies.essentialCookiesTitle')}
            </h2>
            <p 
              className="mb-6 leading-relaxed"
              style={{ color: 'var(--th-text-secondary)' }}
            >
              {t('cookies.essentialCookiesIntro')}
            </p>

            <div className="grid gap-4 sm:grid-cols-2">
              {cookieTypes.map((cookie, index) => (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                  className="p-5 rounded-xl border-2 transition-all hover:shadow-lg"
                  style={{ 
                    backgroundColor: 'var(--th-card-bg)',
                    borderColor: 'var(--th-card-border)'
                  }}
                  data-testid={`cookie-type-${index}`}
                >
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                    style={{ backgroundColor: `${cookie.color}20` }}
                  >
                    <cookie.icon 
                      className="w-6 h-6" 
                      style={{ color: cookie.color }}
                    />
                  </div>
                  <h3 
                    className="font-heading text-lg font-bold mb-2"
                    style={{ color: 'var(--th-text-primary)' }}
                  >
                    {t(`cookies.${cookie.titleKey}`)}
                  </h3>
                  <p 
                    className="text-sm leading-relaxed"
                    style={{ color: 'var(--th-text-secondary)' }}
                  >
                    {t(`cookies.${cookie.descKey}`)}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.section>

          {/* Your Choices Section */}
          <motion.section variants={fadeInUp} className="mb-10">
            <h2 
              className="font-heading text-2xl font-bold mb-4"
              style={{ color: 'var(--th-text-primary)' }}
            >
              {t('cookies.yourChoicesTitle')}
            </h2>
            <p 
              className="leading-relaxed"
              style={{ color: 'var(--th-text-secondary)' }}
            >
              {t('cookies.yourChoicesDesc')}
            </p>
          </motion.section>

          {/* Contact Section */}
          <motion.section variants={fadeInUp}>
            <h2 
              className="font-heading text-2xl font-bold mb-4"
              style={{ color: 'var(--th-text-primary)' }}
            >
              {t('cookies.contactTitle')}
            </h2>
            <p style={{ color: 'var(--th-text-secondary)' }}>
              {t('cookies.contactDesc')}{' '}
              <a 
                href="mailto:oscar@teacherhappy.com"
                className="font-semibold hover:underline"
                style={{ color: 'var(--th-secondary)' }}
              >
                oscar@teacherhappy.com
              </a>
            </p>
          </motion.section>

          {/* Back to Home Button */}
          <motion.div variants={fadeInUp} className="mt-12 text-center">
            <Link 
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold transition-all hover:scale-105 shadow-lg"
              style={{ 
                backgroundColor: 'var(--th-primary)',
                color: '#1E293B'
              }}
              data-testid="cookies-cta-home"
            >
              <ArrowLeft className="w-5 h-5" />
              {t('cookies.backToHome')}
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};
